// Every One AI site, and the URL shape of each — the one place a product's own
// address is written down.
//
// THIS IS NOT THE FOOTER'S TABLE, which is why it sits in org/ rather than in
// ui/site-footer/. The footer is its largest consumer, but each
// app's lib/i18n/config.ts takes its `locales` and `localePrefix` from here
// too, and through those so does every canonical, sitemap entry and hreflang
// tag the three sites emit. AGENTS.md's "a folder per component holding
// everything that component is" governs ui/; this is not a component.
//
// It replaces two tables that had already drifted apart: apps/one-ai's
// `lib/product-sites.ts` (the SERVICE nav panel and the /service cards) and the
// `PRODUCT_COLUMNS` literal that used to sit inside its TheFooter.tsx.
//
// THE RULE THE WHOLE FOOTER RUNS ON. Given the site rendering a link (`self`)
// and the site the link belongs to (`owner`):
//
//     owner === self   →  prefix[locale] + path                    internal
//     owner !== self   →  origin[locale] + prefix[locale] + path   external
//
// Two irregular cases fall out of it without special-casing: OneCatch sits on a
// shared host under a per-locale path segment, while One AI, OneLive and ChiChat
// carry our own `{jp: "", zh: "/zh"}` scheme. Nothing here is derived from
// anything else, which is why it is a table and not a function.
//
// AN EMPTY `origin` MEANS "NOT DECIDED YET", and every absolute URL into that
// site resolves to `null` — see `siteUrl`. Concatenating it instead would turn
// `https://chichat…/` into a bare `/`, which on another site is a link to THAT
// site's own home page.

export const SITE_LOCALES = ["zh", "jp"] as const;

export type SiteLocale = (typeof SITE_LOCALES)[number];

export type SiteKey = "oneai" | "onelive" | "chichat" | "onecatch";

/**
 * Every route any consumer needs an address for. Deliberately NOT an inventory
 * of each site's routes — a site declares a key only where something links to
 * it, which is why `onelive` has no `service` entry despite serving /service.
 */
export type SitePathKey =
    // The three rows every product column draws, in order.
    | "home"
    | "flow"
    | "cases"
    // The 公司官網 column, in nav order.
    | "about"
    | "service"
    | "news"
    | "blog"
    | "recruit"
    // The CONTACT band's pill.
    | "contact";

export type Site = {
    /** Wordmark as the footer draws it. The same string in both locales. */
    name: string;
    /**
     * Scheme + host, per locale, with no trailing slash. `""` while the site's
     * domain is undecided — every absolute URL into it is then `null`.
     */
    origin: Record<SiteLocale, string>;
    /** Path segment before every route, per locale. May be "". */
    prefix: Record<SiteLocale, string>;
    /**
     * The routes this table exposes, as authored — locale-agnostic and always
     * leading-slashed. A key absent here is a row the footer DROPS, which is
     * what replaces the `url: ""` sentinel the old PRODUCT_COLUMNS used to mean
     * "this product has no such page".
     */
    paths: Partial<Record<SitePathKey, string>>;
};

export const SITES: Record<SiteKey, Site> = {
    oneai: {
        name: "One AI",
        // PLACEHOLDER — one-ai's production hostname is not decided. This
        // restates FALLBACK_SITE_URL in apps/one-ai/src/lib/seo/config.ts
        // rather than reading NEXT_PUBLIC_SITE_URL, because onelive and chichat
        // need One AI's origin at build time in THEIR builds, where that
        // variable names a different site. THE TWO MUST BE CHANGED TOGETHER.
        origin: { zh: "https://one-ai.inc", jp: "https://one-ai.inc" },
        prefix: { zh: "/zh", jp: "" },
        paths: {
            home: "/",
            about: "/about",
            service: "/service",
            news: "/news",
            blog: "/blog",
            recruit: "/recruit",
            contact: "/contact",
        },
    },
    onelive: {
        name: "OneLive",
        origin: { zh: "https://one-live.ai", jp: "https://one-live.ai" },
        prefix: { zh: "/zh", jp: "" },
        paths: {
            home: "/",
            flow: "/business",
            cases: "/case",
            contact: "/contact",
        },
    },
    chichat: {
        name: "ChiChat",
        // One origin for both locales, confirmed 2026-09-29: jp at the bare
        // paths, zh under /zh — the scheme One AI and OneLive use. It replaces
        // the old per-locale .tw/.tech split, whose TLD-to-language mapping was
        // never settled.
        //
        // apps/chichat's lib/seo/config.ts falls back to this when
        // NEXT_PUBLIC_SITE_URL is unset, so it is the one place ChiChat's host
        // is written down: changing it moves ChiChat's own canonicals AND the
        // other two sites' links into ChiChat (after each is rebuilt).
        origin: { zh: "https://chichat.tech", jp: "https://chichat.tech" },
        prefix: { zh: "/zh", jp: "" },
        paths: {
            home: "/",
            flow: "/service",
            // Dead until the route lands — chichat links at it knowingly rather
            // than sending its visitors to another site's contact form.
            contact: "/contact",
        },
    },
    onecatch: {
        name: "OneCatch",
        origin: { zh: "https://one-design.ai", jp: "https://one-design.ai" },
        prefix: { zh: "/one-catch_zh", jp: "/one-catch_jp" },
        paths: {
            home: "/",
        },
    },
};

/**
 * Root-relative and locale-prefixed: `("oneai", "zh", "about")` → `"/zh/about"`.
 * `null` when the site declares no such route.
 *
 * Identical to each app's `localeHref(path, locale)`, INCLUDING its one special
 * case: the home path collapses to the bare prefix (`"/zh"`, not `"/zh/"`) so
 * the link matches the route the app actually serves.
 */
export function sitePath(
    site: SiteKey,
    locale: SiteLocale,
    key: SitePathKey,
): string | null {
    const { prefix, paths } = SITES[site];
    const path = paths[key];
    if (path === undefined) return null;

    const localePrefix = prefix[locale];
    if (!localePrefix) return path;
    return path === "/" ? localePrefix : `${localePrefix}${path}`;
}

/**
 * Absolute: `("onelive", "zh", "flow")` → `"https://one-live.ai/zh/business"`.
 * `null` when the site declares no such route, OR when its origin is still
 * undecided (`""`) — a root-relative string here would silently point at
 * whichever site is rendering it.
 *
 * Plain concatenation, so a home URL keeps its trailing slash
 * (`"https://one-live.ai/zh/"`) — this is NOT `origin + sitePath()`, which
 * would drop it. Both resolve to the same page; the slash is kept because it is
 * what one-ai's footer has been emitting.
 */
export function siteUrl(
    site: SiteKey,
    locale: SiteLocale,
    key: SitePathKey,
): string | null {
    const { origin, prefix, paths } = SITES[site];
    const path = paths[key];
    if (path === undefined || !origin[locale]) return null;
    return `${origin[locale]}${prefix[locale]}${path}`;
}

export type FooterLink = {
    /** Ready to put on an `href`. Root-relative when internal, absolute when not. */
    href: string;
    /** Internal rows go through the injected `next/link`; external rows are `<a target="_blank">`. */
    external: boolean;
    /**
     * The route as authored (`"/about"`), unprefixed. Only an internal row's
     * liveness can be tested, and `isPathLive` — apps/one-ai's `isNavPathLive`
     * — is written against the authored path, not the prefixed one.
     */
    path: string;
};

export type FooterColumn =
    /**
     * 公司官網. Five rows, positionally aligned with the dictionary's
     * `footer.columns.site.items` and therefore with `NAV_ITEMS`.
     */
    | { kind: "site"; links: (FooterLink & { fallbackLabel: string })[] }
    /**
     * One product. Three slots positionally aligned with
     * `footer.columns.product.items`; `null` is a page that product does not
     * have, and the row is dropped rather than rendered as dead text.
     */
    | { kind: "product"; name: string; links: (FooterLink | null)[] };

/** The 公司官網 column's rows, in the comps' order — the primary nav's order. */
const SITE_COLUMN_ROWS: { key: SitePathKey; fallbackLabel: string }[] = [
    { key: "about", fallbackLabel: "ABOUT" },
    { key: "service", fallbackLabel: "SERVICE" },
    { key: "news", fallbackLabel: "NEWS" },
    { key: "blog", fallbackLabel: "BLOG" },
    { key: "recruit", fallbackLabel: "RECRUIT" },
];

/** The three rows every product column draws, in order. */
const PRODUCT_COLUMN_ROWS: SitePathKey[] = ["home", "flow", "cases"];

/** The product columns, in the order the comps draw them. */
const PRODUCT_COLUMN_SITES: SiteKey[] = ["onelive", "chichat", "onecatch"];

function footerLink(
    self: SiteKey,
    owner: SiteKey,
    locale: SiteLocale,
    key: SitePathKey,
): FooterLink | null {
    const path = SITES[owner].paths[key];
    if (path === undefined) return null;

    if (owner === self) {
        // Non-null: `paths[key]` is what `sitePath` keys off, and it is set.
        return { href: sitePath(owner, locale, key)!, external: false, path };
    }
    // Null when the owner's origin is undecided: the row is dropped, exactly
    // like a route the owner does not declare.
    const href = siteUrl(owner, locale, key);
    return href === null ? null : { href, external: true, path };
}

/**
 * The footer's four columns, resolved for the site rendering them.
 *
 * ALWAYS four, ALWAYS in this order — 公司官網, OneLive, ChiChat, OneCatch. The
 * set does not vary by consumer; only which column's rows are internal does,
 * which is the whole of goal 3. On apps/one-ai every product column is external
 * and 公司官網 is internal (exactly today's behaviour); on apps/chichat the
 * ChiChat column is internal and 公司官網 links out.
 *
 * Both column kinds return POSITIONAL arrays, because both take their labels
 * from a dictionary array by index — that is how the comps draw them and how
 * the footer has always read them.
 */
export function buildFooterColumns(
    self: SiteKey,
    locale: SiteLocale,
): FooterColumn[] {
    const site: FooterColumn = {
        kind: "site",
        links: SITE_COLUMN_ROWS.flatMap(({ key, fallbackLabel }) => {
            const link = footerLink(self, "oneai", locale, key);
            return link ? [{ ...link, fallbackLabel }] : [];
        }),
    };

    const products: FooterColumn[] = PRODUCT_COLUMN_SITES.map((owner) => ({
        kind: "product",
        name: SITES[owner].name,
        links: PRODUCT_COLUMN_ROWS.map((key) =>
            footerLink(self, owner, locale, key),
        ),
    }));

    return [site, ...products];
}
